import { Controller } from "@hotwired/stimulus"

// Preserves the table's scroll position (horizontal + vertical) across Turbo
// Frame re-renders (sort / filter / paginate). The scroll container lives inside
// the frame and is rebuilt on every render, so without this the table jumps back
// to the top-left — most visibly a horizontal "shift" once you've scrolled right.
//
// Attach to the scroll container (the overflow-auto div), NOT the <table>, which
// does not scroll itself. Saves on scroll; restores when the (new) element
// connects after a render, keyed per data-scroll-memory-storage-key-value so
// multiple tables on one page don't collide.
export default class extends Controller {
  static values = { storageKey: String }

  connect() {
    this.onScroll = this.onScroll.bind(this)
    this.element.addEventListener("scroll", this.onScroll, { passive: true })
    this.restore()
  }

  disconnect() {
    this.element.removeEventListener("scroll", this.onScroll)
  }

  onScroll() {
    if (!this.hasStorageKeyValue) return
    sessionStorage.setItem(this.storageKeyValue, `${this.element.scrollLeft},${this.element.scrollTop}`)
  }

  restore() {
    if (!this.hasStorageKeyValue) return
    const saved = sessionStorage.getItem(this.storageKeyValue)
    if (!saved) return
    const [left, top] = saved.split(",").map((n) => parseInt(n, 10) || 0)
    // On a full page return (row → detail → back) the table only reaches its
    // full scroll width once the columns controller and fonts settle, so a
    // single rAF can fire while scrollLeft is still clamped to 0 — losing the
    // horizontal position. Re-apply only *while the content is still growing*
    // toward that size, and stop the moment the offset sticks OR the content
    // size stops changing (settled, or the saved offset is genuinely
    // unreachable on shorter content). This avoids both burning frames on an
    // unreachable target and fighting a user who scrolls once layout is stable.
    let prevWidth = null
    let prevHeight = null
    let attempts = 0
    const apply = () => {
      this.element.scrollLeft = left
      this.element.scrollTop = top
      const reached =
        Math.abs(this.element.scrollLeft - left) <= 1 &&
        Math.abs(this.element.scrollTop - top) <= 1
      const growing =
        this.element.scrollWidth !== prevWidth || this.element.scrollHeight !== prevHeight
      prevWidth = this.element.scrollWidth
      prevHeight = this.element.scrollHeight
      if (!reached && growing && (attempts += 1) < 60) requestAnimationFrame(apply)
    }
    requestAnimationFrame(apply)
  }
}
